/*
 * ===============================================================================================================
 *                                Copyright 2024, Blue Yonder Group, Inc.
 *                                           All Rights Reserved
 *
 *                               THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
 *                                          BLUE YONDER GROUP, INC.
 *
 *
 *                         The copyright notice above does not evidence any actual
 *                                 or intended publication of such source code.
 *
 * ===============================================================================================================
 */
import { hideLoader } from "../loader/loader";
import { SNACKBAR_ALERT_DEFAULT_PROPS } from "../../shared/constants";
import { setSnackBarDisplay } from "../snackBar/snackBarActionCreator";
import { getValidationErrorMessages } from "../../utility/commonUtility";
import { showErrorAction, clearErrorAction } from "../error/errorActionCreator";
import { hideLoaderAction, showLoaderAction } from "../loader/loaderActionCreator";
import { getAllOrganizationsAction, setOrgPaginationAction } from "./organizationActionCreator";
import { IGetOrganizationPayload, IOrganization } from "../../models/Organizations/OrganizationContracts";
import { createNewOrganization, deleteOrganization, getAllOrganizations, updateOrganization } from "../../services/organization";

export const getOrganizations = async (dispatch: Function, organizationPayload: IGetOrganizationPayload) => {
  let organizations: IOrganization[] = [];
  let selectedOrganization: any = {};
  try {
    dispatch(showLoaderAction());
    dispatch(clearErrorAction());
    const organizationsResponse = await getAllOrganizations(organizationPayload);
    if (organizationsResponse.data) {
      organizations = organizationsResponse.data;
      selectedOrganization = organizations.find((org: any) => org.name === 'Blue Yonder') || organizations[0];
      if (organizationPayload.id) {
        selectedOrganization = organizations.find((org: any) => org.id === organizationPayload.id);
      }
      dispatch(getAllOrganizationsAction(organizations, selectedOrganization, organizations.length > 0 ? false : true));
    }
    dispatch(setOrgPaginationAction(organizationsResponse.metadata));
    dispatch(hideLoader);
  } catch (error) {
    let errorMessageDetail = getValidationErrorMessages(error);
    const errorMessage = {
      message: "Error in fetching Organizations: " + errorMessageDetail,
    };
    organizations = [];
    selectedOrganization = {};
    dispatch(getAllOrganizationsAction(organizations, selectedOrganization, true));
    dispatch(showErrorAction(errorMessage));
    dispatch(hideLoader);
    dispatch(
      setSnackBarDisplay({
        ...SNACKBAR_ALERT_DEFAULT_PROPS,
        show: true,
        message: 'Error in fetching Organizations: ' + errorMessageDetail,
        severity: 'error',
      })
    );
  }
}

export const createOrganization = async (dispatch: Function, organizationPayload: any) => {
  try {
    dispatch(showLoaderAction());
    dispatch(clearErrorAction());
    const createOrganizationResponse = await createNewOrganization(organizationPayload);
    await getOrganizations(dispatch, { pageNo: 0, pageSize: 500, searchString: "", id: createOrganizationResponse.id });
    if (createOrganizationResponse.id) {
      getOrganizations(dispatch, {});
    }
    dispatch(hideLoaderAction());
    dispatch(
      setSnackBarDisplay({
        ...SNACKBAR_ALERT_DEFAULT_PROPS,
        show: true,
        severity: 'success',
        message: 'Organization created successfully',
      })
    );
    return true;
  } catch (error: any) {
    let errorMessageDetail = getValidationErrorMessages(error);
    const errorMessage = {
      message: 'Error in creating Organization: ' + errorMessageDetail
    };
    dispatch(showErrorAction(errorMessage));
    dispatch(hideLoaderAction());
    dispatch(
      setSnackBarDisplay({
        ...SNACKBAR_ALERT_DEFAULT_PROPS,
        show: true,
        severity: 'error',
        message: 'Error in creating Organization: ' + errorMessageDetail,
      })
    );
    return false;
  }
};

export const editOrganization = async (dispatch: Function, organizationPayload: any) => {
  try {
    dispatch(showLoaderAction());
    dispatch(clearErrorAction());
    await updateOrganization(organizationPayload);
    getOrganizations(dispatch, { id: organizationPayload.id });
    dispatch(hideLoaderAction());
    dispatch(
      setSnackBarDisplay({
        ...SNACKBAR_ALERT_DEFAULT_PROPS,
        show: true,
        severity: 'success',
        message: 'Organization updated successfully',
      })
    );
    return true;
  } catch (error: any) {
    const errorMessageDetail = getValidationErrorMessages(error);
    const errorMessage = {
      message: 'Error in Edit Organization: ' + errorMessageDetail
    };
    dispatch(showErrorAction(errorMessage));
    dispatch(hideLoaderAction());
    dispatch(
      setSnackBarDisplay({
        ...SNACKBAR_ALERT_DEFAULT_PROPS,
        show: true,
        severity: 'error',
        message: 'Error in Edit Organization: ' + errorMessageDetail,
      })
    );
    return false;
  }
};

export const removeOrganization = async (dispatch: Function, organizationId: string) => {
  try {
    dispatch(showLoaderAction());
    dispatch(clearErrorAction());
    await deleteOrganization(organizationId);
    getOrganizations(dispatch, { pageNo: 0, pageSize: 500, searchString: "" });
    dispatch(hideLoaderAction());
  } catch (error: any) {
    const errorMessageDetail = getValidationErrorMessages(error);
    const errorMessage = {
      message: 'Error in fetching data: ' + errorMessageDetail
    };
    dispatch(showErrorAction(errorMessage));
    dispatch(hideLoaderAction());
    dispatch(
      setSnackBarDisplay({
        ...SNACKBAR_ALERT_DEFAULT_PROPS,
        show: true,
        severity: 'error',
        message: 'Error in Delete organization: ' + errorMessageDetail,
      })
    );
  }
};